import { z } from 'zod';
import { CustomFieldValueSchema } from './custom-field-value.dto';
import {
  CustomerAllowedEmailDomainsSchema,
  UpdateCustomerAllowedEmailDomainsSchema,
} from './customer-allowed-email-domains.dto';
import { inferApiResultSchema, inferPaginatedApiResponse } from './infer-api';

export const CustomerSchema = z.object({
  id: z.number(),
  accountId: z.string().max(50),
  name: z.string().trim().min(1).max(300),
  logoKey: z.string().nullable(),
  allowedEmailDomains: z.array(CustomerAllowedEmailDomainsSchema).optional(),
  fields: z.array(CustomFieldValueSchema),
  owner: z.string().max(50).nullish(),
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.coerce.date().nullish(),
  archivedAt: z.coerce.date().nullish(),
});

const CustomerWithLogoSchema = CustomerSchema.extend({ logoUrl: z.string().nullable() });

export type CustomerDto = z.infer<typeof CustomerWithLogoSchema>;
export type CustomerNoLogoDto = z.infer<typeof CustomerSchema>;

// POST /api/v1/customers
export const CreateCustomerSchema = CustomerSchema.omit({
  id: true,
  accountId: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
  archivedAt: true,
  allowedEmailDomains: true,
}).extend({
  logoKey: z.string().nullable().optional(),
  fields: z.array(CustomFieldValueSchema).default([]).optional(),
});

export type CreateCustomerDto = z.infer<typeof CreateCustomerSchema>;
export const CreateCustomerResponseSchema = inferApiResultSchema(
  CustomerSchema.omit({ allowedEmailDomains: true }),
);
export type CreateCustomerResponseDto = z.infer<typeof CreateCustomerResponseSchema>;

export const CustomerSortSchema = z.object({
  column: CustomerSchema.omit({
    accountId: true,
    fields: true,
    allowedEmailDomains: true,
    owner: true,
  })
    .keyof()
    .default('createdAt'),
  order: z.enum(['asc', 'desc']).default('desc'),
});
export type CustomerSortDto = z.infer<typeof CustomerSortSchema>;

export const CustomerFilterSchema = CustomerSchema.omit({
  accountId: true,
  fields: true,
  allowedEmailDomains: true,
  owner: true,
}).partial();
export type CustomerFilterDto = z.infer<typeof CustomerFilterSchema>;

// GET /api/v1/customers/:id
export const GetCustomerByIdResponseSchema = inferApiResultSchema(CustomerWithLogoSchema);
export type GetCustomerByIdResponseDto = z.infer<typeof GetCustomerByIdResponseSchema>;

export const GetCustomersByIdsResponseSchema = inferApiResultSchema(
  z.array(CustomerSchema.omit({ allowedEmailDomains: true })),
);
export type GetCustomersByIdsResponseDto = z.infer<typeof GetCustomersByIdsResponseSchema>;

// PUT /api/v1/customers/:id
export const UpdateCustomerSchema = CustomerSchema.omit({
  id: true,
  accountId: true,
  createdAt: true,
  updatedAt: true,
  archivedAt: true,
})
  .extend({
    allowedEmailDomains: z.array(UpdateCustomerAllowedEmailDomainsSchema).optional(),
  })
  .partial();
export type UpdateCustomerDto = z.infer<typeof UpdateCustomerSchema>;
export const UpdateCustomerResponseSchema = inferApiResultSchema(CustomerWithLogoSchema.partial());
export type UpdateCustomerResponseDto = z.infer<typeof UpdateCustomerResponseSchema>;

export const UpdateCustomerTableSchema = UpdateCustomerSchema.omit({
  // The allowedEmailDomains is in its own table now so we
  // need to omit it for updating the customers table
  allowedEmailDomains: true,
}).partial();

// GET /api/v1/customers
export const GetCustomersResponseSchema = inferPaginatedApiResponse(z.array(CustomerSchema));
export type GetCustomersResponseDto = z.infer<typeof GetCustomersResponseSchema>;
